'use client'

import { QueryClientProvider } from '@tanstack/react-query'
import { getQueryClient } from '@/lib/tanstack/queryClient'
import React, { useState } from 'react'
import dynamic from 'next/dynamic'

// Dynamically import ReactQueryDevtools to avoid SSR issues
const ReactQueryDevtools = dynamic(
  () => import('@tanstack/react-query-devtools').then((mod) => mod.ReactQueryDevtools),
  { ssr: false },
)

/**
 * QueryProvider wraps the application with Tanstack Query's QueryClientProvider
 *
 * This component:
 * - Creates a QueryClient instance on mount (ensures fresh client per request)
 * - Provides the QueryClient to all child components
 * - Includes ReactQueryDevtools in development for debugging
 */
export function QueryProvider({ children }: { children: React.ReactNode }) {
  // Create QueryClient once on mount to ensure stable reference
  const [queryClient] = useState(() => getQueryClient())

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </QueryClientProvider>
  )
}
